import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { DEFAULT_SETTINGS } from '../lib/defaults'
import { ConfirmModal, NameModal } from './Modals'

const STATUSES = ['enquiry', 'live', 'paused', 'complete']

// v2 status styles — inline only where CSS classes alone can't cover dynamic values
const STATUS_STYLE = {
  enquiry: { background: 'color-mix(in srgb,var(--c-adhesives) 12%,var(--surface))', color: 'var(--c-adhesives)', border: 'var(--c-adhesives)' },
  live:    { background: 'var(--green-soft)', color: 'var(--green)', border: 'var(--green)' },
  paused:  { background: 'var(--surface-3)',  color: 'var(--ink-3)', border: 'var(--line-2)' },
  complete:{ background: 'var(--green-soft)', color: 'var(--green-deep)', border: 'var(--green-deep)' },
}

// Column template shared by header and data rows
const COLS = '44px 1fr 90px 140px 1fr 78px'

export default function ProjectList({ session }) {
  const [templates, setTemplates] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [menuOpen, setMenuOpen] = useState(null)
  const [statusSubmenuOpen, setStatusSubmenuOpen] = useState(null)
  const [renaming, setRenaming] = useState(null)   // { id, isTemplate }
  const [renameValue, setRenameValue] = useState('')
  const [confirmModal, setConfirmModal] = useState(null)
  const [nameModal, setNameModal] = useState(null)
  const [newProjectName, setNewProjectName] = useState('')
  const [addingProject, setAddingProject] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const menuRef = useRef(null)
  const newInputRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => { loadAll() }, [])

  useEffect(() => {
    if (addingProject && newInputRef.current) newInputRef.current.focus()
  }, [addingProject])

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const loadAll = async () => {
    const { data, error } = await supabase
      .from('projects').select('*')
      .eq('user_id', session.user.id)
      .order('name')
    if (!error && data) {
      setTemplates(data.filter(p => p.is_template))
      setProjects(data.filter(p => !p.is_template).sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      ))
    }
    setLoading(false)
  }

  const getUserSettings = async () => {
    const { data } = await supabase.from('settings').select('*').eq('user_id', session.user.id).single()
    return data || DEFAULT_SETTINGS
  }

  // ── Create blank project ────────────────────────────────────────
  const createBlankProject = async (name) => {
    if (!name.trim()) return
    const settings = await getUserSettings()
    const { data: project, error } = await supabase.from('projects').insert({
      user_id: session.user.id,
      name: name.trim(),
      status: 'enquiry',
      is_template: false,
      day_rate: settings.day_rate || 350,
      hours_per_day: settings.hours_per_day || 8,
      material_markup_pct: settings.material_markup_pct ?? 20,
      labour_markup_pct: settings.labour_markup_pct || 0,
      vat_registered: settings.vat_registered || false,
      vat_rate: settings.vat_rate ?? 20,
    }).select().single()
    if (error || !project) return

    // Blank project — completely empty, no pre-stamped tasks or labour lines
    setAddingProject(false)
    setNewProjectName('')
    navigate(`/projects/${project.id}`)
  }

  // ── Open as New Project (copy) ──────────────────────────────────
  const saveAsNewProject = async (sourceId) => {
    setMenuOpen(null)
    setNameModal({
      title: 'Name for new project',
      placeholder: 'Project name…',
      confirmLabel: 'Create',
      onConfirm: async (name) => {
        const { data: items } = await supabase.from('line_items').select('*').eq('project_id', sourceId).order('sort_order')
        const { data: labour } = await supabase.from('labour_lines').select('*').eq('project_id', sourceId).order('sort_order')
        const { data: tasks } = await supabase.from('time_tasks').select('*').eq('project_id', sourceId).order('sort_order')
        const { data: source } = await supabase.from('projects').select('*').eq('id', sourceId).single()
        const { data: project, error } = await supabase.from('projects').insert({
          user_id: session.user.id,
          name: name.trim(),
          status: 'enquiry',
          is_template: false,
          day_rate: source?.day_rate || 350,
          hours_per_day: source?.hours_per_day || 8,
          material_markup_pct: source?.material_markup_pct ?? 20,
          labour_markup_pct: source?.labour_markup_pct || 0,
          vat_registered: source?.vat_registered || false,
          vat_rate: source?.vat_rate ?? 20,
        }).select().single()
        if (error || !project) return
        const strip = ({ id, project_id, created_at, updated_at, ...rest }) => ({ ...rest, project_id: project.id })
        if (items?.length)  await supabase.from('line_items').insert(items.map(strip))
        if (labour?.length) await supabase.from('labour_lines').insert(labour.map(strip))
        if (tasks?.length)  await supabase.from('time_tasks').insert(tasks.map(strip))
        navigate(`/projects/${project.id}`)
      }
    })
  }

  const _saveAsNewProject_UNUSED = null // removed

  // ── Save project as template ────────────────────────────────────
  const saveAsTemplate = async (sourceId) => {
    setMenuOpen(null)
    setNameModal({
      title: 'Name this template',
      placeholder: 'Template name…',
      confirmLabel: 'Save template',
      onConfirm: async (name) => {
        const { data: items } = await supabase.from('line_items').select('*').eq('project_id', sourceId).order('sort_order')
        const { data: labour } = await supabase.from('labour_lines').select('*').eq('project_id', sourceId).order('sort_order')
        const { data: tasks } = await supabase.from('time_tasks').select('*').eq('project_id', sourceId).order('sort_order')
        const { data: source } = await supabase.from('projects').select('*').eq('id', sourceId).single()

        const { data: tmpl, error } = await supabase.from('projects').insert({
          user_id: session.user.id,
          name: name.trim(),
          status: 'active',
          is_template: true,
          day_rate: source?.day_rate || 350,
          hours_per_day: source?.hours_per_day || 8,
          material_markup_pct: source?.material_markup_pct ?? 20,
          labour_markup_pct: source?.labour_markup_pct || 0,
          vat_registered: source?.vat_registered || false,
          vat_rate: source?.vat_rate ?? 20,
        }).select().single()
        if (error || !tmpl) return

        const strip = ({ id, project_id, created_at, updated_at, ...rest }) => ({ ...rest, project_id: tmpl.id })
        if (items?.length)  await supabase.from('line_items').insert(items.map(strip))
        if (labour?.length) await supabase.from('labour_lines').insert(labour.map(strip))
        if (tasks?.length)  await supabase.from('time_tasks').insert(tasks.map(strip))

        setTemplates(prev => [...prev, tmpl].sort((a, b) => a.name.localeCompare(b.name)))
      }
    })
  }

  // ── Rename ──────────────────────────────────────────────────────
  const startRename = (id, currentName, isTemplate) => {
    setRenaming({ id, isTemplate })
    setRenameValue(currentName)
    setMenuOpen(null)
  }

  const saveRename = async () => {
    if (!renaming || !renameValue.trim()) { setRenaming(null); return }
    await supabase.from('projects').update({ name: renameValue.trim() }).eq('id', renaming.id)
    if (renaming.isTemplate) {
      setTemplates(prev => prev.map(t => t.id === renaming.id ? { ...t, name: renameValue.trim() } : t))
    } else {
      setProjects(prev => prev.map(p => p.id === renaming.id ? { ...p, name: renameValue.trim() } : p))
    }
    setRenaming(null)
  }

  // ── Change status ───────────────────────────────────────────────
  const changeStatus = async (id, status) => {
    await supabase.from('projects').update({ status }).eq('id', id)
    setProjects(prev => prev.map(p => p.id === id ? { ...p, status } : p))
    setMenuOpen(null)
    setStatusSubmenuOpen(null)
  }

  // ── Delete ──────────────────────────────────────────────────────
  const deleteItem = async (id, name, isTemplate) => {
    setMenuOpen(null)
    setConfirmModal({
      message: `Delete "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await supabase.from('projects').delete().eq('id', id)
        if (isTemplate) setTemplates(prev => prev.filter(t => t.id !== id))
        else setProjects(prev => prev.filter(p => p.id !== id))
      }
    })
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '4rem', fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--ink-3)', letterSpacing: '2px' }}>
      Loading…
    </div>
  )

  const Row = ({ item, isTemplate, isLast }) => {
    const isMenuOpen = menuOpen === item.id
    const isRenaming = renaming?.id === item.id
    const isStatusOpen = statusSubmenuOpen === item.id
    const statusStyle = !isTemplate && STATUS_STYLE[item.status]

    return (
      <div
        className="ak-row"
        style={{
          '--cols': isTemplate ? '1fr' : COLS,
          height: 'auto',
          minHeight: 'var(--row-h)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          borderRadius: isLast ? '0 0 var(--radius) var(--radius)' : undefined,
          boxShadow: isLast ? 'inset 0 1px 0 var(--line)' : undefined,
        }}
      >
        {/* Main clickable area */}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: isTemplate ? '1fr' : COLS,
            alignItems: 'center',
            columnGap: '12px',
            cursor: 'pointer',
            minWidth: 0,
          }}
          onClick={() => !isRenaming && navigate(`/projects/${item.id}`)}
        >
          {isTemplate ? (
            <span className="ak-cell-name">{item.name}</span>
          ) : (
            <>
              {/* Ref */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {item.proposal_number && (
                  <span style={{
                    fontFamily: 'var(--mono)',
                    fontSize: '10px',
                    color: 'var(--ink-3)',
                    background: 'var(--surface-3)',
                    border: '1px solid var(--line-2)',
                    borderRadius: '3px',
                    padding: '1px 5px',
                  }}>
                    {item.proposal_number}
                  </span>
                )}
              </div>

              {/* Name */}
              <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                {isRenaming ? (
                  <input
                    className="ak-input"
                    style={{ flex: 1, fontSize: '13.5px', fontWeight: 500 }}
                    value={renameValue}
                    autoFocus
                    onChange={e => setRenameValue(e.target.value)}
                    onBlur={saveRename}
                    onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null) }}
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span className="ak-cell-name">{item.name}</span>
                )}
              </div>

              {/* Status */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {statusStyle && (
                  <span style={{
                    fontFamily: 'var(--mono)',
                    fontSize: '9.5px',
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: '20px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    border: '1px solid',
                    flexShrink: 0,
                    background: statusStyle.background,
                    color: statusStyle.color,
                    borderColor: statusStyle.border,
                  }}>
                    {item.status}
                  </span>
                )}
              </div>

              {/* Client */}
              <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                {item.client_name && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.client_name}
                  </span>
                )}
              </div>

              {/* Address */}
              <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
                {item.client_address && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.client_address}
                  </span>
                )}
              </div>

              {/* Date */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {item.created_at && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink-3)' }}>
                    {new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Actions: menu + arrow */}
        <div
          style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          ref={isMenuOpen ? menuRef : null}
        >
          <button
            className="ak-iconbtn"
            style={{ fontSize: '1rem', letterSpacing: '1px', color: 'var(--ink-3)' }}
            onClick={e => { e.stopPropagation(); setMenuOpen(isMenuOpen ? null : item.id); setStatusSubmenuOpen(null) }}
          >⋯</button>

          {isMenuOpen && (
            <div className="ak-pop" style={{ right: 0, top: 'calc(100% + 4px)' }}>
              <button onClick={e => { e.stopPropagation(); saveAsNewProject(item.id) }}>
                Open as New Project
              </button>
              {!isTemplate && (
                <button onClick={e => { e.stopPropagation(); saveAsTemplate(item.id) }}>
                  Save as template
                </button>
              )}
              <div style={{ height: '1px', background: 'var(--line)', margin: '3px 0' }} />
              {!isTemplate && (
                <>
                  <button
                    style={{ justifyContent: 'space-between' }}
                    onClick={e => { e.stopPropagation(); setStatusSubmenuOpen(isStatusOpen ? null : item.id) }}
                  >
                    <span>Change status</span>
                    <span style={{ fontSize: '0.55rem', color: 'var(--ink-3)' }}>{isStatusOpen ? '▲' : '▼'}</span>
                  </button>
                  {isStatusOpen && (
                    <div style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)', paddingTop: '2px', paddingBottom: '2px' }}>
                      {STATUSES.map(st => {
                        const ss = STATUS_STYLE[st]
                        const isCurrent = item.status === st
                        return (
                          <button
                            key={st}
                            style={{
                              background: isCurrent ? 'var(--green-soft)' : undefined,
                              paddingLeft: '18px',
                            }}
                            onClick={e => { e.stopPropagation(); changeStatus(item.id, st) }}
                          >
                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: ss.color, flexShrink: 0, display: 'inline-block' }} />
                            {st.charAt(0).toUpperCase() + st.slice(1)}
                            {isCurrent && <span style={{ marginLeft: 'auto', color: 'var(--green)', fontSize: '0.75rem' }}>✓</span>}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  <div style={{ height: '1px', background: 'var(--line)', margin: '3px 0' }} />
                </>
              )}
              <button onClick={e => { e.stopPropagation(); startRename(item.id, item.name, isTemplate) }}>
                Rename
              </button>
              {!isTemplate && (
                <button onClick={e => {
                  e.stopPropagation()
                  setMenuOpen(null)
                  setNameModal({
                    title: 'Project reference',
                    placeholder: 'e.g. P001',
                    confirmLabel: 'Save',
                    defaultValue: item.proposal_number || '',
                    onConfirm: async (val) => {
                      await supabase.from('projects').update({ proposal_number: val.trim() }).eq('id', item.id)
                      setProjects(prev => prev.map(p => p.id === item.id ? { ...p, proposal_number: val.trim() } : p))
                    }
                  })
                }}>
                  Edit reference
                </button>
              )}
              <div style={{ height: '1px', background: 'var(--line)', margin: '3px 0' }} />
              <button style={{ color: 'var(--c-additional)' }} onClick={e => { e.stopPropagation(); deleteItem(item.id, item.name, isTemplate) }}>
                Delete
              </button>
            </div>
          )}

          <span
            style={{ color: 'var(--ink-3)', fontSize: '1rem', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => navigate(`/projects/${item.id}`)}
          >→</span>
        </div>
      </div>
    )
  }

  return (
    <div className="ak-app">
      <ConfirmModal config={confirmModal} onClose={() => setConfirmModal(null)} />
      <NameModal config={nameModal} onClose={() => setNameModal(null)} />

      {/* Header */}
      <div className="ak-page-head" style={{ marginBottom: '1.75rem' }}>
        <h1 className="t-page">Projects</h1>
        <button className="ak-btn ak-btn--primary" onClick={() => {
          setAddingProject(true)
          setMenuOpen(null)
        }}>+ New project</button>
      </div>

      {/* New project inline input */}
      {addingProject && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--surface)',
          border: '1.5px solid var(--green)',
          borderRadius: 'var(--radius)',
          marginBottom: '1rem',
          overflow: 'hidden',
        }}>
          <input
            ref={newInputRef}
            className="ak-input"
            style={{ flex: 1, border: 'none', borderRadius: 0, background: 'transparent', padding: '0.65rem 1rem', fontSize: '13.5px' }}
            value={newProjectName}
            onChange={e => setNewProjectName(e.target.value)}
            placeholder="Project name…"
            onKeyDown={e => {
              if (e.key === 'Enter') createBlankProject(newProjectName)
              if (e.key === 'Escape') { setAddingProject(false); setNewProjectName('') }
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', paddingRight: '0.75rem' }}>
            <button className="ak-btn ak-btn--primary ak-btn--sm" onClick={() => createBlankProject(newProjectName)}>
              Create
            </button>
            <button className="ak-btn ak-btn--ghost ak-btn--sm" onClick={() => { setAddingProject(false); setNewProjectName('') }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Templates section */}
      {templates.length > 0 && (
        <div style={{ marginBottom: '1.75rem' }}>
          <div className="t-label" style={{ marginBottom: '0.5rem' }}>Templates</div>
          <div className="ak-card" style={{ overflow: 'visible' }}>
            {templates.map((t, i) => <Row key={t.id} item={t} isTemplate={true} isLast={i === templates.length - 1} />)}
          </div>
        </div>
      )}

      {/* Projects section */}
      {projects.length > 0 ? (
        <div>
          {/* Section label + filter tabs */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <div className="t-label">Projects</div>
            <div className="ak-seg">
              {['all', ...STATUSES].map(tab => {
                const count = tab === 'all' ? projects.length : projects.filter(p => p.status === tab).length
                return (
                  <button
                    key={tab}
                    className={filterStatus === tab ? 'on' : ''}
                    onClick={() => setFilterStatus(tab)}
                  >
                    {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    <span className="ak-badge" style={{ marginLeft: '4px', fontSize: '10px', padding: '0 5px' }}>{count}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Column header strip */}
          <div
            className="ak-table-head"
            style={{ '--cols': COLS, borderRadius: 'var(--radius) var(--radius) 0 0', display: 'flex', alignItems: 'center', padding: '0 16px' }}
          >
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: COLS, columnGap: '12px', alignItems: 'center' }}>
              <span>Ref</span>
              <span>Project</span>
              <span>Status</span>
              <span>Client</span>
              <span>Address</span>
              <span>Created</span>
            </div>
            {/* Phantom spacer to align with row actions */}
            <div style={{ visibility: 'hidden', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button className="ak-iconbtn">⋯</button>
              <span style={{ fontSize: '1rem' }}>→</span>
            </div>
          </div>

          {/* Project rows */}
          <div className="ak-card" style={{ borderRadius: '0 0 var(--radius) var(--radius)', borderTop: 'none', overflow: 'visible' }}>
            {projects
              .filter(p => filterStatus === 'all' || p.status === filterStatus)
              .map((p, i, arr) => <Row key={p.id} item={p} isTemplate={false} isLast={i === arr.length - 1} />)}
          </div>
        </div>
      ) : !addingProject && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <p style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>No projects yet</p>
          <p style={{ color: 'var(--ink-2)', fontSize: '13.5px', marginBottom: '1.5rem' }}>
            Create your first project to get started.
          </p>
          <button className="ak-btn ak-btn--primary" onClick={() => setAddingProject(true)}>
            Create your first project →
          </button>
        </div>
      )}
    </div>
  )
}
